import { z } from 'zod'
import { MAXIMUM_ALIAS_LENGTH, MAXIMUM_NAME_LENGTH } from './accountAliases.ts'
import { ACCOUNT_IDENTITY_PROVIDERS, normalizedPhoneNumberSchema } from './accountIdentity.ts'
import { normalizedEmailAddressSchema } from './emailIdentity.ts'
import { SUPPORTED_LOCALES } from './supportedLocales.ts'

export const MAXIMUM_MESSAGE_BODY_LENGTH = 20_000
export const MAXIMUM_ATTACHMENTS_PER_MESSAGE = 10
export const DEFAULT_MESSAGE_HISTORY_PAGE_SIZE = 30
export const MAXIMUM_MESSAGE_HISTORY_PAGE_SIZE = 100

export const supportedLocaleSchema = z.enum(SUPPORTED_LOCALES)

/** The two schemes somebody can ask for. Anything else means following the device. */
export const colorSchemeSchema = z.enum(['light', 'dark'])
export type ColorScheme = z.infer<typeof colorSchemeSchema>

export const attachmentKindSchema = z.enum(['image', 'audio', 'video', 'pdf', 'file'])
export type AttachmentKind = z.infer<typeof attachmentKindSchema>

/** The notification sounds `scripts/generateNotificationSound.mjs` writes into the app. */
export const NOTIFICATION_SOUND_NAMES = ['chime', 'ping', 'bell', 'knock', 'pop'] as const
export const notificationSoundNameSchema = z.enum(NOTIFICATION_SOUND_NAMES)
export type NotificationSoundName = z.infer<typeof notificationSoundNameSchema>

export function buildNotificationSoundPath(soundName: NotificationSoundName): string {
  return `/sounds/${soundName}.wav`
}

// ---------------------------------------------------------------------------
// Authentication and account
// ---------------------------------------------------------------------------

export const accountIdentitySchema = z.object({
  provider: z.enum(ACCOUNT_IDENTITY_PROVIDERS),
  subject: z.string(),
  verifiedAt: z.string().nullable(),
})

/** Renaming somebody, and what comes back: who they are, and what you now call them. */
export const setAccountAliasRequestSchema = z.object({
  alias: z.string().trim().min(1).max(MAXIMUM_ALIAS_LENGTH),
})

export const accountAliasSchema = z.object({
  accountId: z.string(),
  handle: z.string(),
  alias: z.string(),
})
export type AccountAlias = z.infer<typeof accountAliasSchema>

export const authenticatedUserSchema = z.object({
  accountId: z.string(),
  /** Which identity stands for this account: its email address, or its phone number. */
  handle: z.string(),
  /** The name this account gave itself. `null` until there is an address to read one from. */
  alias: z.string().nullable(),
  /**
   * Every way this account can be reached and proved. A list rather than a fixed pair,
   * because a person may arrive by address, by number, or through a provider.
   */
  identities: z.array(accountIdentitySchema),
  /** What a provider says this person is called; overwritten on every sign-in. */
  displayName: z.string().nullable(),
  /** The name this person gave themselves, which no provider ever rewrites. */
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  preferredLocale: supportedLocaleSchema.nullable(),
  /**
   * Light or dark. `null` means follow the device, and it belongs to the account rather
   * than to the browser — two accounts open in two tabs are two different people.
   */
  preferredColorScheme: colorSchemeSchema.nullable(),
})
export type AuthenticatedUser = z.infer<typeof authenticatedUserSchema>

export const updatePreferredColorSchemeRequestSchema = z.object({
  preferredColorScheme: colorSchemeSchema.nullable(),
})

/**
 * Editing one's own profile. Every field is optional and only what is sent is changed, so
 * uploading a picture never silently clears a name.
 */
export const updateProfileRequestSchema = z
  .object({
    firstName: z.string().max(MAXIMUM_NAME_LENGTH).nullish(),
    lastName: z.string().max(MAXIMUM_NAME_LENGTH).nullish(),
    /** A receipt from the ordinary upload endpoint, which already holds the bytes. */
    avatarUploadReceiptToken: z.string().min(16).max(256).nullish(),
  })
  .refine(
    (profileUpdate) =>
      profileUpdate.firstName !== undefined ||
      profileUpdate.lastName !== undefined ||
      profileUpdate.avatarUploadReceiptToken !== undefined,
    { message: 'A profile update has to change something.' },
  )
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>

export const maximumUploadBytesByAttachmentKindSchema = z.object({
  image: z.number().int().positive(),
  audio: z.number().int().positive(),
  video: z.number().int().positive(),
  file: z.number().int().positive(),
})
export type MaximumUploadBytesByAttachmentKind = z.infer<
  typeof maximumUploadBytesByAttachmentKindSchema
>

/**
 * Where the local development tools live, or `null` when this deployment exposes none.
 *
 * `null` is the default and the only thing a real deployment ever sends. What these links
 * feed — account identifiers, internal addresses, the state of the current route — is for
 * whoever is building the application and for nobody else, so the deployment has to say
 * yes rather than merely fail to say no.
 */
export const developmentToolsSchema = z.object({
  /** Where LocalStack serves the text messages it captured; Mailpit's counterpart for SMS. */
  smsInboxUrl: z.string().nullable(),
  /** Where Mailpit serves the email it captured. */
  emailInboxUrl: z.string().nullable(),
})
export type DevelopmentTools = z.infer<typeof developmentToolsSchema>

/**
 * A login code or link this deployment just sent, shown in the development panel so a
 * developer does not have to open an inbox to sign in.
 *
 * These are working credentials. They are served only while the deployment turns the
 * development tools on, and the route that carries them does not exist otherwise.
 */
export const developmentLoginCodeSchema = z.object({
  channel: z.enum(['sms', 'email']),
  /** Who it was sent to: a phone number, or an email address. */
  handle: z.string(),
  /** What the code is for, since a sign-in code and a number confirmation are not alike. */
  purpose: z.string(),
  /** The four digit code, for a text message. */
  loginCode: z.string().nullable(),
  /** The link, for an email. */
  loginUrl: z.string().nullable(),
  issuedAt: z.string(),
})
export type DevelopmentLoginCode = z.infer<typeof developmentLoginCodeSchema>

export const developmentLoginCodeListSchema = z.object({
  loginCodes: z.array(developmentLoginCodeSchema),
})

/**
 * One text message delivery as the operator panel shows it: when it was sent, who it was
 * for and why, with the carrier answer beside it — or nulls where no report arrived yet.
 *
 * Operator tooling, in English only like the development panel: the operator reads the
 * same words the software uses internally.
 */
export const operatorSmsDeliverySchema = z.object({
  snsMessageId: z.string(),
  phoneNumber: z.string(),
  purpose: z.string().nullable(),
  recipientAccountId: z.string().nullable(),
  sentAt: z.string(),
  status: z.string().nullable(),
  providerResponse: z.string().nullable(),
  phoneCarrier: z.string().nullable(),
  messageParts: z.number().int().nullable(),
  priceInUsd: z.string().nullable(),
  deviceAcknowledgedAfterMs: z.number().int().nullable(),
  reportedAt: z.string().nullable(),
})
export type OperatorSmsDelivery = z.infer<typeof operatorSmsDeliverySchema>

export const operatorSmsDeliveryListSchema = z.object({
  deliveries: z.array(operatorSmsDeliverySchema),
})
export type OperatorSmsDeliveryList = z.infer<typeof operatorSmsDeliveryListSchema>

export const operatorSmsDeliveryQuerySchema = z.object({
  /** A handle to narrow the list to one person. Absent means the most recent of all. */
  handle: z.string().trim().min(1).max(320).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
})
export type OperatorSmsDeliveryQuery = z.infer<typeof operatorSmsDeliveryQuerySchema>

/**
 * Banning and lifting name an account by handle, exactly as the operator typed it. The
 * lookup behind both never creates an account: an unknown handle matches nothing.
 */
export const operatorAccountBanRequestSchema = z.object({
  handle: z.string().trim().min(1).max(320),
})
export type OperatorAccountBanRequest = z.infer<typeof operatorAccountBanRequestSchema>

export const operatorAccountSchema = z.object({
  accountId: z.string(),
  handle: z.string(),
  /** ISO timestamp of the ban, or null for an account in good standing. */
  bannedAt: z.string().nullable(),
})
export type OperatorAccount = z.infer<typeof operatorAccountSchema>

// ---------------------------------------------------------------------------
// Administration
// ---------------------------------------------------------------------------

/** One account as the administration sees it: every identity, never an alias. */
export const adminAccountSchema = z.object({
  accountId: z.string(),
  /** The canonical handle: the email address when it has one, else the phone number. */
  handle: z.string(),
  identities: z.array(accountIdentitySchema),
  createdAt: z.string(),
  confirmedAt: z.string().nullable(),
  bannedAt: z.string().nullable(),
})
export type AdminAccount = z.infer<typeof adminAccountSchema>

export const adminAccountListSchema = z.object({
  accounts: z.array(adminAccountSchema),
  /** Opaque cursor for the next page, absent when the list runs out. */
  nextCursor: z.string().nullable(),
})
export type AdminAccountList = z.infer<typeof adminAccountListSchema>

export const adminAccountListQuerySchema = z.object({
  /** Matches handles and identity subjects containing this text, case-insensitively. */
  search: z.string().trim().min(1).max(320).optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
})

/** One conversation as the administration sees it: ids and handles, never aliases. */
export const adminConversationSchema = z.object({
  counterpartAccountId: z.string(),
  counterpartHandle: z.string().nullable(),
  isGroup: z.boolean(),
  groupName: z.string().nullable(),
  lastMessageAt: z.string().nullable(),
  lastMessagePreview: z.string().nullable(),
})
export type AdminConversation = z.infer<typeof adminConversationSchema>

export const adminConversationListSchema = z.object({
  conversations: z.array(adminConversationSchema),
})

/** One notification burst with what each channel managed, for whoever was to be told. */
export const adminNotificationSchema = z.object({
  notificationId: z.number().int().positive(),
  recipientAccountId: z.string(),
  recipientHandle: z.string(),
  targetDisplayName: z.string(),
  isGroup: z.boolean(),
  messageCount: z.number().int().nonnegative(),
  pushedAt: z.string().nullable(),
  emailedAt: z.string().nullable(),
  textedAt: z.string().nullable(),
  readAt: z.string().nullable(),
  createdAt: z.string(),
})
export type AdminNotification = z.infer<typeof adminNotificationSchema>

export const adminNotificationListSchema = z.object({
  notifications: z.array(adminNotificationSchema),
  nextCursor: z.string().nullable(),
})
export type AdminNotificationList = z.infer<typeof adminNotificationListSchema>

export const adminNotificationListQuerySchema = z.object({
  handle: z.string().trim().min(1).max(320).optional(),
  channel: z.enum(['email', 'sms', 'push']).optional(),
  /** `delivered` means the channel answered; `pending` means it never did. */
  delivery: z.enum(['delivered', 'pending']).optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
})

export const authenticationStatusSchema = z.object({
  authenticatedUser: authenticatedUserSchema.nullable(),
  googleAuthenticationEnabled: z.boolean(),
  phoneAuthenticationEnabled: z.boolean(),
  // The language this deployment serves. `null` marks a multilingual deployment, the only
  // kind where browser detection and a stored preference still decide the language.
  deploymentLocale: supportedLocaleSchema.nullable(),
  deploymentProductName: z.string().nullable(),
  magicLinkExpiryMinutes: z.number().int().positive(),
  webPushPublicKey: z.string().nullable(),
  notificationSoundName: notificationSoundNameSchema,
  maximumUploadBytesByAttachmentKind: maximumUploadBytesByAttachmentKindSchema,
  // Which country code to assume for a phone number typed without one, decided by this
  // deployment's language and where the request came from. `null` when no rule covers it,
  // in which case a number must carry its own.
  defaultCallingCode: z.string().nullable(),
  // `null` on every deployment that does not deliberately turn the tools on.
  developmentTools: developmentToolsSchema.nullable(),
  /**
   * Whether the signed-in account may open the operator panel. Decided by the server from
   * `ADMIN_ACCOUNTS` on every status load, so the menu never learns the list itself.
   */
  operatorPanelEnabled: z.boolean(),
})
export type AuthenticationStatus = z.infer<typeof authenticationStatusSchema>

/** A device an account is signed in on, and can be signed out of. */
export const signedInDeviceSchema = z.object({
  deviceLabel: z.string().nullable(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  isCurrentDevice: z.boolean(),
})
export type SignedInDevice = z.infer<typeof signedInDeviceSchema>

export const signedInDeviceListSchema = z.object({
  devices: z.array(signedInDeviceSchema),
})

// ---------------------------------------------------------------------------
// Signing in with a phone number
// ---------------------------------------------------------------------------

export const PHONE_LOGIN_CODE_LENGTH = 4
export const LOGIN_CODE_EXPIRY_MINUTES = 15

export const emailLoginCodeChallengeSchema = z.object({
  challengeId: z.string().uuid(),
  expiresAt: z.string(),
})
export const verifyEmailLoginCodeSchema = z.object({
  challengeId: z.string().uuid(),
  loginCode: z.string().regex(/^\d{4}$/),
})

export const phoneLoginCodeSchema = z
  .string()
  .trim()
  .regex(/^[0-9]{4}$/, 'A login code is four digits.')

export const requestPhoneLoginCodeRequestSchema = z.object({
  phoneNumber: normalizedPhoneNumberSchema,
})
export type RequestPhoneLoginCodeRequest = z.infer<typeof requestPhoneLoginCodeRequestSchema>

export const requestPhoneLoginCodeResponseSchema = z.object({
  phoneNumber: z.string(),
  expiresAt: z.string(),
})

export const verifyPhoneLoginCodeRequestSchema = z.object({
  phoneNumber: normalizedPhoneNumberSchema,
  loginCode: phoneLoginCodeSchema,
})
export type VerifyPhoneLoginCodeRequest = z.infer<typeof verifyPhoneLoginCodeRequestSchema>

/**
 * A code is verified by a `fetch`, so unlike a link the session token comes straight back
 * in the response body and never needs to travel through a URL.
 */
export const phoneLoginCompletionSchema = z.object({
  sessionToken: z.string(),
  authenticatedUser: authenticatedUserSchema,
})
export type PhoneLoginCompletion = z.infer<typeof phoneLoginCompletionSchema>

/**
 * Adding another way of reaching or proving an account, whatever kind it is.
 *
 * The handle arrives as typed, because a phone number may still be missing its country
 * code at this point and only the server knows which one to assume.
 */
export const attachIdentityRequestSchema = z.object({
  handle: z.string().min(1).max(320),
})
export type AttachIdentityRequest = z.infer<typeof attachIdentityRequestSchema>

/**
 * How the address is being proved: a number receives four digits to retype, an address
 * receives a link to open. That asymmetry is the whole reason this says which.
 */
export const identityVerificationChallengeSchema = z.object({
  handle: z.string(),
  channel: z.enum(['sms', 'email']),
  expiresAt: z.string(),
})
export type IdentityVerificationChallenge = z.infer<typeof identityVerificationChallengeSchema>

export const confirmIdentityRequestSchema = z.object({
  handle: z.string().min(1).max(320),
  loginCode: phoneLoginCodeSchema,
})
export type ConfirmIdentityRequest = z.infer<typeof confirmIdentityRequestSchema>

export const requestMagicLinkRequestSchema = z.object({
  emailAddress: normalizedEmailAddressSchema,
  redirectPath: z.string().max(2048).optional(),
})
export type RequestMagicLinkRequest = z.infer<typeof requestMagicLinkRequestSchema>
export const requestEmailLoginCodeSchema = requestMagicLinkRequestSchema
export const emailLoginCompletionSchema = phoneLoginCompletionSchema.extend({
  redirectPath: z.string(),
})

export const updatePreferredLocaleRequestSchema = z.object({
  preferredLocale: supportedLocaleSchema,
})

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export const messageAttachmentSchema = z.object({
  attachmentId: z.number().int().positive(),
  originalFilename: z.string(),
  contentType: z.string(),
  byteSize: z.number().int().nonnegative(),
  contentSha256Hex: z.string(),
  attachmentPosition: z.number().int().nonnegative(),
  attachmentKind: attachmentKindSchema,
  contentPath: z.string(),
})
export type MessageAttachment = z.infer<typeof messageAttachmentSchema>

export const chatMessageSchema = z.object({
  messageId: z.number().int().positive(),
  senderHandle: z.string(),
  /**
   * `null` in a group, where there is no single person it was written to: the recipients are
   * whoever is a member at the moment somebody reads it, which is not a fact about the
   * message. A message to one person still names them.
   */
  recipientHandle: z.string().nullable(),
  parentMessageId: z.number().int().positive().nullable(),
  body: z.string().nullable(),
  createdAt: z.string(),
  readAt: z.string().nullable(),
  attachments: z.array(messageAttachmentSchema),
  replyCount: z.number().int().nonnegative(),
  unreadReplyCount: z.number().int().nonnegative(),
})
export type ChatMessage = z.infer<typeof chatMessageSchema>

/**
 * Changing what somebody chose about one conversation. Each switch is sent on its own, so
 * muting never quietly archives and archiving never quietly mutes.
 */
export const conversationPreferenceRequestSchema = z.object({
  muted: z.boolean().optional(),
  archived: z.boolean().optional(),
})
export type ConversationPreferenceRequest = z.infer<typeof conversationPreferenceRequestSchema>

export const conversationPreferenceSchema = z.object({
  isMuted: z.boolean(),
  isArchived: z.boolean(),
})
export type ConversationPreference = z.infer<typeof conversationPreferenceSchema>

export const conversationSummarySchema = z.object({
  /**
   * Who the conversation is with. A rename names an account, not an address.
   *
   * For a group this carries the **group id**, which is why an account and a group are drawn
   * from one keyspace: the URL segment, the router and the service worker take an id and
   * cannot tell the two apart, so nothing downstream had to learn a second shape.
   */
  counterpartAccountId: z.string(),
  /** `null` for a group, which is addressed by its id and has no handle to be written to. */
  counterpartHandle: z.string().nullable(),
  /** The person's name as this viewer knows them, or the group's name, which is shared. */
  counterpartDisplayName: z.string().nullable(),
  /** Which of the two this row is, so a screen never has to guess from a missing handle. */
  isGroup: z.boolean(),
  counterpartAvatarUrl: z.string().nullable(),
  /**
   * The three below are `null` for a group nobody has written in yet.
   *
   * A conversation with one person is *born* of a message, so it always had one. A group is
   * born of being created, and must still appear in the list from that moment — otherwise it
   * vanishes the instant somebody makes it.
   */
  lastMessageId: z.number().int().positive().nullable(),
  lastMessageAt: z.string().nullable(),
  lastMessageSenderHandle: z.string().nullable(),
  lastMessagePreview: z.string().nullable(),
  lastMessageHasAttachments: z.boolean(),
  unreadMessageCount: z.number().int().nonnegative(),
  /**
   * What this viewer chose about this conversation, and nobody else's business: a muted or
   * archived conversation looks exactly the same to the person on the other side.
   *
   * An archived conversation still carries its unread count. Archiving decides which list it
   * belongs in, not whether anything arrived.
   */
  isMuted: z.boolean(),
  isArchived: z.boolean(),
})
export type ConversationSummary = z.infer<typeof conversationSummarySchema>

export const conversationListSchema = z.object({
  conversations: z.array(conversationSummarySchema),
})

/**
 * One thing a person missed, as they will read it.
 *
 * Every name here is resolved for whoever is reading: the row itself holds codes, because the
 * name somebody gave a contact is theirs alone and must never travel to anybody else.
 */
export const notificationSchema = z.object({
  notificationId: z.number().int().positive(),
  /** What happened. A message arrived, or somebody put this person in a group. */
  kind: z.enum(['message', 'added-to-group']),
  /** Where it happened: a person's conversation, or a group. Both are addressable codes. */
  targetId: z.string(),
  targetDisplayName: z.string(),
  isGroup: z.boolean(),
  /** Who wrote last, or who did the adding. */
  actorDisplayName: z.string(),
  /** How many messages the burst gathered. One for anything that is not a message. */
  messageCount: z.number().int().positive(),
  occurredAt: z.string(),
  readAt: z.string().nullable(),
})
export type AppNotification = z.infer<typeof notificationSchema>

export const notificationPageSchema = z.object({
  notifications: z.array(notificationSchema),
  /** The badge, counted where the rows are, so there is only ever one answer to it. */
  unreadCount: z.number().int().nonnegative(),
  /** Absent once the history runs out, which is how a screen knows to stop asking. */
  olderThanNotificationId: z.number().int().positive().nullable(),
})
export type NotificationPage = z.infer<typeof notificationPageSchema>

export const messageHistoryPageSchema = z.object({
  /**
   * `null` for a group, which is addressed by its id and has no handle to be written to. A
   * conversation with one person still names them.
   */
  counterpartHandle: z.string().nullable(),
  /**
   * The code this conversation belongs to, or `null` while writing to an address that
   * belongs to nobody yet — there is no account to name until the first message creates one.
   */
  counterpartAccountId: z.string().nullable(),
  counterpartDisplayName: z.string().nullable(),
  messages: z.array(chatMessageSchema),
  hasOlderMessages: z.boolean(),
})
export type MessageHistoryPage = z.infer<typeof messageHistoryPageSchema>

export const threadViewSchema = z.object({
  /** `null` in a group: a thread there is in the group, not with one other person. */
  counterpartHandle: z.string().nullable(),
  /** The group's shared name, when the thread is in one. */
  groupName: z.string().nullable(),
  rootMessage: chatMessageSchema,
  replies: z.array(chatMessageSchema),
})
export type ThreadView = z.infer<typeof threadViewSchema>

/** One message with where it belongs, for the global recent view. */
export const adminMessageEntrySchema = z.object({
  message: chatMessageSchema,
  groupId: z.string().nullable(),
  groupName: z.string().nullable(),
})
export type AdminMessageEntry = z.infer<typeof adminMessageEntrySchema>

export const adminMessageListSchema = z.object({
  messages: z.array(adminMessageEntrySchema),
  nextCursor: z.string().nullable(),
})
export type AdminMessageList = z.infer<typeof adminMessageListSchema>

export const adminMessageListQuerySchema = z.object({
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
})

/** One conversation read by an operator: whose, and with whom or in which group. */
export const adminConversationMessagesQuerySchema = z.object({
  accountId: z.string().min(1).max(64),
  counterpartId: z.string().min(1).max(64),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
})

export const messageLocationSchema = z.object({
  messageId: z.number().int().positive(),
  counterpartHandle: z.string(),
  /** The code the conversation URL is built from; the handle is what a person reads. */
  counterpartAccountId: z.string(),
  counterpartAlias: z.string().nullable(),
  rootMessageId: z.number().int().positive(),
  isThreadReply: z.boolean(),
})
export type MessageLocation = z.infer<typeof messageLocationSchema>

/**
 * A group: a place with a name, which outlives the people in it.
 *
 * The name is the **first shared name in this product**. Every other name somebody sees is an
 * alias belonging to one viewer and hidden from everybody else, which is why this one travels
 * in a payload at all.
 *
 * The members are account ids and their names *as this viewer knows them*: a name one member
 * gave another is a fact about the one who gave it, and a list computed once and handed to
 * everybody would open every member's address book to the rest.
 */
export const GROUP_NAME_MAXIMUM_LENGTH = 80

export const groupMemberSchema = z.object({
  accountId: z.string(),
  handle: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
})
export type GroupMember = z.infer<typeof groupMemberSchema>

export const groupViewSchema = z.object({
  groupId: z.string(),
  name: z.string(),
  members: z.array(groupMemberSchema),
})
export type GroupView = z.infer<typeof groupViewSchema>

export const createGroupRequestSchema = z.object({
  name: z.string().trim().min(1).max(GROUP_NAME_MAXIMUM_LENGTH),
  /** Whoever is named here joins at once. Anybody may add anybody: no permission is asked. */
  memberHandles: z.array(z.string()).max(50).optional(),
})
export type CreateGroupRequest = z.infer<typeof createGroupRequestSchema>

/**
 * How far somebody has read in a group. A group has as many readers as members, so reading
 * is a high-water mark on the reader rather than a flag on each message.
 */
export const markGroupReadRequestSchema = z.object({
  lastReadMessageId: z.number().int().positive(),
})
export type MarkGroupReadRequest = z.infer<typeof markGroupReadRequestSchema>

export const renameGroupRequestSchema = z.object({
  name: z.string().trim().min(1).max(GROUP_NAME_MAXIMUM_LENGTH),
})
export type RenameGroupRequest = z.infer<typeof renameGroupRequestSchema>

export const addGroupMemberRequestSchema = z.object({
  /** An address, because somebody with no account yet is added by writing to them. */
  handle: z.string().min(1),
})
export type AddGroupMemberRequest = z.infer<typeof addGroupMemberRequestSchema>

export const attachmentReferenceInputSchema = z.object({
  uploadReceiptToken: z.string().min(16).max(256),
  originalFilename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255),
})
export type AttachmentReferenceInput = z.infer<typeof attachmentReferenceInputSchema>

export const sendMessageRequestSchema = z
  .object({
    body: z.string().max(MAXIMUM_MESSAGE_BODY_LENGTH).nullish(),
    attachments: z
      .array(attachmentReferenceInputSchema)
      .max(MAXIMUM_ATTACHMENTS_PER_MESSAGE)
      .optional(),
  })
  .refine(
    (sendMessageRequest) =>
      (sendMessageRequest.body ?? '').trim().length > 0 ||
      (sendMessageRequest.attachments?.length ?? 0) > 0,
    { message: 'A message needs a body or at least one attachment.' },
  )
export type SendMessageRequest = z.infer<typeof sendMessageRequestSchema>

export const markMessagesReadRequestSchema = z.object({
  messageIds: z.array(z.number().int().positive()).min(1).max(500),
})

export const messageHistoryQuerySchema = z.object({
  beforeMessageId: z.coerce.number().int().positive().optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAXIMUM_MESSAGE_HISTORY_PAGE_SIZE)
    .default(DEFAULT_MESSAGE_HISTORY_PAGE_SIZE),
})

export const uploadedAttachmentSchema = z.object({
  uploadReceiptToken: z.string(),
  contentSha256Hex: z.string(),
  byteSize: z.number().int().nonnegative(),
  originalFilename: z.string(),
  contentType: z.string(),
  attachmentKind: attachmentKindSchema,
})
export type UploadedAttachment = z.infer<typeof uploadedAttachmentSchema>

export const uploadAttachmentResponseSchema = z.object({
  uploadedAttachments: z.array(uploadedAttachmentSchema),
})

// ---------------------------------------------------------------------------
// Web Push
// ---------------------------------------------------------------------------

export const webPushSubscriptionRequestSchema = z.object({
  endpoint: z.string().url().max(2048),
  expirationTime: z.number().nullable().optional(),
  keys: z.object({
    p256dh: z.string().min(1).max(256),
    auth: z.string().min(1).max(256),
  }),
})
export type WebPushSubscriptionRequest = z.infer<typeof webPushSubscriptionRequestSchema>

export const webPushUnsubscribeRequestSchema = z.object({
  endpoint: z.string().url().max(2048),
})

export const pushNotificationPayloadSchema = z.object({
  notificationTitle: z.string(),
  notificationBody: z.string(),
  deepLinkPath: z.string(),
  messageId: z.number().int().positive(),
  /** Which signed in account this notification belongs to. */
  accountHandle: z.string(),
  counterpartHandle: z.string(),
  notificationTag: z.string(),
})
export type PushNotificationPayload = z.infer<typeof pushNotificationPayloadSchema>

// ---------------------------------------------------------------------------
// Real-time events
// ---------------------------------------------------------------------------

export const realtimeEventSchema = z.discriminatedUnion('eventType', [
  z.object({
    eventType: z.literal('message-created'),
    messageId: z.number().int().positive(),
    /** `null` in a group: the conversation is named by its id, not by a person's address. */
    counterpartHandle: z.string().nullable(),
    /** The code the conversation URL is built from; the handle is what a person reads. */
    counterpartAccountId: z.string(),
    counterpartAlias: z.string().nullable(),
    rootMessageId: z.number().int().positive(),
    isThreadReply: z.boolean(),
    senderHandle: z.string(),
    // Carried on the event so an incoming message can be announced immediately, without
    // a round trip that would delay the sound and the notification.
    senderDisplayName: z.string(),
    messagePreview: z.string(),
  }),
  z.object({
    eventType: z.literal('messages-read'),
    counterpartHandle: z.string(),
  }),
])
export type RealtimeEvent = z.infer<typeof realtimeEventSchema>

export const apiErrorResponseSchema = z.object({
  errorCode: z.string(),
  localizationKey: z.string(),
})
export type ApiErrorResponse = z.infer<typeof apiErrorResponseSchema>
